import { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Table,
} from 'reactstrap';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { initialValues, validationSchema } from './constants';
import './CreateRole.scss';
import { fetchPrivileges, saveRole } from '../../../../store/apis/adminService';

function CreateRole({ isOpen, toggle, handleRoleUpdated }) {
  const [formKey, setFormKey] = useState(0);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  // Fetch privileges from API
  const { data: privileges, isLoading: isLoadingPrivileges } = useQuery({
    queryKey: ['privileges'],
    queryFn: fetchPrivileges,
  });

  // Setup field array for privilegeIds
  const { fields } = useFieldArray({
    control,
    name: 'privilegeIds',
  });

  // Populate privilegeIds when privileges are loaded
  useEffect(() => {
    if (privileges && Array.isArray(privileges)) {
      setFormKey(prev => prev + 1); // force rerender
      reset({
        ...initialValues,
        privilegeIds: privileges.map(p => ({
          privilegeId: p.ID,
          status: false,
        })),
      });
    }
  }, [privileges, reset, isOpen]);

  // Mutation for creating a role
  const { mutate: createRoleMutate, isPending: isCreating } = useMutation({
    mutationFn: saveRole,
    onSuccess: () => {
      toast.success('Role Created Successfully!', {
        className: 'toast-success',
      });
      reset(initialValues); // Reset form after submission
      toggle();
      handleRoleUpdated();
    },
    onError: err => {
      toast.error(err?.message || 'Failed to create role', {
        className: 'toast-error',
      });
    },
  });

  const onSubmit = values => {
    const payload = {
      roleName: values.roleName,
      isActive: values.isActive === 'true',
      privilegeIds: values.privilegeIds
        .filter(p => p.status)
        .map(p => p.privilegeId),
    };
    createRoleMutate(payload);
  };

  const onCancel = () => {
    toggle();
    reset(initialValues);
  };

  // Render privilege body with validation
  const renderPrivilegeBody = () =>
    fields.map((item, index) => (
      <tr key={item.id}>
        <td>{privileges?.find(p => p.ID === item.privilegeId)?.Name || ''}</td>
        <td>
          <Controller
            control={control}
            name={`privilegeIds.${index}.status`}
            rules={{ required: true }}
            render={({ field }) => (
              <FormGroup switch>
                <Input
                  id={`switch-${index}`}
                  type="switch"
                  {...field}
                  checked={field.value}
                  className={`form-check-input ${errors?.privilegeIds?.[index]?.status ? 'is-invalid' : ''}`}
                />
                <label className="ms-2 fw-light" htmlFor={`switch-${index}`}>
                  {field.value ? 'Yes' : 'No'}
                </label>
                {errors?.privilegeIds?.[index]?.status && (
                  <div className="invalid-feedback d-block">Required</div>
                )}
              </FormGroup>
            )}
          />
        </td>
      </tr>
    ));

  return (
    <Offcanvas
      className="create-role-oc"
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
    >
      <OffcanvasHeader className="create-role-oc-header">
        Create Role
      </OffcanvasHeader>
      <Form
        key={formKey}
        onSubmit={handleSubmit(onSubmit)}
        className="create-role-form"
      >
        <OffcanvasBody className="create-role-oc-body">
          <div className="d-flex flex-column gap-3">
            {/* Role Name */}
            <div>
              <Label>
                Role Name <span className="text-danger">*</span>
              </Label>
              <Controller
                name="roleName"
                control={control}
                render={({ field }) => (
                  <Input
                    type="text"
                    className={`form-control external-filter-input ${errors?.roleName && 'is-invalid'}`}
                    placeholder="Enter Role Name"
                    {...field}
                  />
                )}
              />
              {errors?.roleName && (
                <div className="invalid-feedback">
                  {errors?.roleName?.message}
                </div>
              )}
            </div>
            {/* Role Status */}
            <div>
              <Label>
                Status <span className="text-danger">*</span>
              </Label>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <Input
                    type="select"
                    className={`form-select external-filter-input ${errors?.isActive && 'is-invalid'}`}
                    {...field}
                  >
                    <option value="">Select Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Input>
                )}
              />
              {errors?.isActive && (
                <div className="invalid-feedback">
                  {errors?.roleName?.message}
                </div>
              )}
            </div>
            {/* Privileges */}
            <div>
              <Label>
                Privileges <span className="text-danger">*</span>
              </Label>
              <div className="table-responsive privileges">
                <Table hover className="custom-table align-middle text-nowrap">
                  <thead>
                    <tr>
                      <th className="fw-semibold">Privileges</th>
                      <th className="fw-semibold">Access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingPrivileges ? (
                      <tr>
                        <td colSpan={2}>Loading privileges...</td>
                      </tr>
                    ) : (
                      renderPrivilegeBody()
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </OffcanvasBody>
        <div className="create-role-oc-footer d-flex flex-row align-items-center gap-2 w-100 p-3">
          <Col>
            <button
              type="button"
              className="create-role-cancel w-100"
              onClick={onCancel}
            >
              Cancel
            </button>
          </Col>
          <Col>
            <button
              type="submit"
              className="create-role-add w-100"
              disabled={!isValid || isCreating}
            >
              {isCreating ? 'Adding...' : 'Add'}
            </button>
          </Col>
        </div>
      </Form>
    </Offcanvas>
  );
}

export default CreateRole;
